import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { usePerformanceMonitoring } from "@/hooks/use-performance";
import { SkipLink } from "@/components/ui/accessible-skip-link";
import { ServiceWorkerRegistration } from "@/components/ServiceWorkerRegistration";
import { initializeAnalytics } from "@/lib/analytics";
import { usePageTracking } from "@/hooks/use-analytics";
import { captureUTMParams } from "@/lib/utm-tracker";
import Index from "./pages/Index";
import EstimatePage from "./pages/EstimatePage";
import Header from "./components/Header";
import Footer from "./components/Footer";

// Lazy load non-critical pages to reduce initial bundle size
const NotFound = lazy(() => import("./pages/NotFound"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const TermsAndConditionsPage = lazy(() => import("./pages/TermsAndConditionsPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const GiftCardPage = lazy(() => import("./pages/GiftCardPage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const ReferralProgramPage = lazy(() => import("./pages/ReferralProgramPage"));

// Lazy load all service pages
const DeepCleaningPage = lazy(() => import("./pages/services/DeepCleaningPage"));
const RegularCleaningPage = lazy(() => import("./pages/services/RegularCleaningPage"));
const MovingCleaningPage = lazy(() => import("./pages/services/MovingCleaningPage"));
const MoveInCleaningPage = lazy(() => import("./pages/services/MoveInCleaningPage"));
const MoveOutCleaningPage = lazy(() => import("./pages/services/MoveOutCleaningPage"));
const OneTimeCleaningPage = lazy(() => import("./pages/services/OneTimeCleaningPage"));
const AirbnbCleaningPage = lazy(() => import("./pages/services/AirbnbCleaningPage"));
const ApartmentCleaningPage = lazy(() => import("./pages/services/ApartmentCleaningPage"));
const HolidayCleaningPage = lazy(() => import("./pages/services/HolidayCleaningPage"));
const SpringCleaningPage = lazy(() => import("./pages/services/SpringCleaningPage"));
const PostConstructionCleaningPage = lazy(() => import("./pages/services/PostConstructionCleaningPage"));
const PostRenovationCleaningPage = lazy(() => import("./pages/services/PostRenovationCleaningPage"));
const WindowCleaningPage = lazy(() => import("./pages/services/WindowCleaningPage"));
const OfficeCleaningPage = lazy(() => import("./pages/services/OfficeCleaningPage"));
const CommercialCleaningPage = lazy(() => import("./pages/services/CommercialCleaningPage"));
const ComparisonPage = lazy(() => import("./pages/services/ComparisonPage"));

// Loading component for lazy-loaded routes
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-[50vh]">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
  </div>
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      gcTime: 1000 * 60 * 30,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

// Component that contains all router-dependent logic
function AppRoutes() {
  usePageTracking();
  usePerformanceMonitoring();

  return (
    <>
      <ServiceWorkerRegistration />
      <SkipLink href="#main-content">Skip to main content</SkipLink>
      <SkipLink href="#services">Skip to services</SkipLink>
      
      <div className="min-h-screen flex flex-col">
        <Header />
        <main id="main-content" className="flex-1">
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/estimate" element={<EstimatePage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditionsPage />} />
              <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
              <Route path="/gift-card" element={<GiftCardPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/referral" element={<ReferralProgramPage />} />
              
              {/* Service Pages */}
              <Route path="/services/comparison" element={<ComparisonPage />} />
              <Route path="/services/deep-cleaning" element={<DeepCleaningPage />} />
              <Route path="/services/regular-cleaning" element={<RegularCleaningPage />} />
              <Route path="/services/moving-cleaning" element={<MovingCleaningPage />} />
              <Route path="/services/move-in-cleaning" element={<MoveInCleaningPage />} />
              <Route path="/services/move-out-cleaning" element={<MoveOutCleaningPage />} />
              <Route path="/services/one-time-cleaning" element={<OneTimeCleaningPage />} />
              <Route path="/services/airbnb-cleaning" element={<AirbnbCleaningPage />} />
              <Route path="/services/apartment-cleaning" element={<ApartmentCleaningPage />} />
              <Route path="/services/holiday-cleaning" element={<HolidayCleaningPage />} />
              <Route path="/services/spring-cleaning" element={<SpringCleaningPage />} />
              <Route path="/services/post-construction-cleaning" element={<PostConstructionCleaningPage />} />
              <Route path="/services/post-renovation-cleaning" element={<PostRenovationCleaningPage />} />
              <Route path="/services/window-cleaning" element={<WindowCleaningPage />} />
              <Route path="/services/office-cleaning" element={<OfficeCleaningPage />} />
              <Route path="/services/commercial-cleaning" element={<CommercialCleaningPage />} />
              
              {/* Redirect old/invalid URLs to home */}
              <Route path="/comments/*" element={<Navigate to="/" replace />} />
              <Route path="/feed/*" element={<Navigate to="/" replace />} />
              <Route path="/apple-app-site-association" element={<Navigate to="/" replace />} />
              <Route path="/*-when-cleaning" element={<Navigate to="/" replace />} />
              <Route path="/which-places-do-i-need-to-pay-more-attention-to-when-cleaning" element={<Navigate to="/" replace />} />
              
              {/* Catch-all route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </main>
        <Footer />
      </div>
    </>
  );
}

function App() {
  useEffect(() => {
    initializeAnalytics();
    captureUTMParams();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}>
          <AppRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
